import {
  Callout,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
  Separator,
  Skeleton,
} from "@/components/ui";
import { QueryKeys } from "@/constants/Query";
import { TRACKERS } from "@/constants/Trackers";
import { useGetPatient } from "@/features/Patient/network/usePatient";
import { captureError } from "@/lib/monitoring";
import { trackEvent } from "@/lib/tracking";
import { useEncounter } from "@/network/useEncounter";
import { useQueryClient } from "@tanstack/react-query";
import { useParams, useSearch } from "@tanstack/react-router";
import { useCallback, useState } from "react";
import { VisitClosureFlow } from "./VisitClosureFlow";
import {
  useCompleteEncounterTime,
  useEncounterCoding,
  useStartEncounterTime,
} from "./network";
import { useVisitClosureSteps } from "./steps";
import {
  VisitClosureDialogContext,
  type VisitClosureDialogLaunch,
} from "./visitClosureDialogContext";

const INITIAL_VISIT_CLOSURE_LAUNCH: VisitClosureDialogLaunch = {
  mode: "billing",
  onSuccess: () => {},
  visitCompleted: false,
};

export const VisitClosureDialogProvider = ({
  children,
}: { children: React.ReactNode }) => {
  const { patientId } = useParams({ from: "/patient/$patientId" });
  const { encounterId } = useSearch({
    from: "/patient/$patientId",
  });

  const [dialogOpen, setDialogOpen] = useState(false);
  const [launch, setLaunch] = useState<VisitClosureDialogLaunch>(
    INITIAL_VISIT_CLOSURE_LAUNCH,
  );
  const queryClient = useQueryClient();

  const { mutateAsync: startEncounterTime } = useStartEncounterTime(
    patientId as string,
    encounterId as string,
  );
  const { mutateAsync: completeEncounterTime } = useCompleteEncounterTime(
    patientId as string,
    encounterId as string,
  );

  const trigger = useCallback(
    async (nextLaunch: VisitClosureDialogLaunch) => {
      setLaunch(nextLaunch);
      // We need to stop the encounter time here before the dialog is rendered
      // so that when we fetch the total encounter time, it will be the correct
      // value.
      try {
        await completeEncounterTime({});
      } catch (error) {
        captureError(error as Error);
      }

      // Always fetch fresh coding and visit diagnoses on open. The mutation hook
      // invalidates only on success, so failures cannot leave stale diagnoses on reopen.
      await queryClient.invalidateQueries({
        queryKey: QueryKeys.Patient.Chart.Billing.ENCOUNTER_CODING({
          patientId: patientId as string,
          encounterId: encounterId as string,
        }),
        exact: true,
      });

      // Tracked immediately before the dialog becomes visible so the paired
      // CLOSE_BILLING_MODAL event measures time in the dialog, not the latency
      // of the request above.
      trackEvent(TRACKERS.OPEN_BILLING_MODAL);
      setDialogOpen(true);
    },
    [completeEncounterTime, encounterId, patientId, queryClient],
  );

  const close = useCallback(async () => {
    trackEvent(TRACKERS.CLOSE_BILLING_MODAL);
    setDialogOpen(false);
    // Start counting encounter time when the dialog is closed
    try {
      await startEncounterTime({});
    } catch (error) {
      captureError(error as Error);
    }
  }, [startEncounterTime]);

  return (
    <VisitClosureDialogContext.Provider value={{ trigger, close }}>
      <VisitClosureDialog
        patientId={patientId as string}
        encounterId={encounterId as string}
        open={dialogOpen}
        // Since the dialog can only be triggered via `trigger`, onOpenChange
        // will always be called with `false` when the dialog is closed
        onOpenChange={close}
        launch={launch}
      />
      {children}
    </VisitClosureDialogContext.Provider>
  );
};

export const VisitClosureDialog = ({
  open,
  onOpenChange,
  launch,
  patientId,
  encounterId,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  launch: VisitClosureDialogLaunch;
  patientId: string;
  encounterId: string;
}) => {
  const [step, setStep] = useState(0);

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) {
      setStep(0);
    }

    onOpenChange(nextOpen);
  };

  const {
    data: patient,
    isLoading: isPatientLoading,
    isError: isPatientError,
    refetch: refetchPatient,
  } = useGetPatient(patientId as string);

  const {
    data: encounterCodingData,
    isFetching: isEncounterCodingFetching,
    isError: isEncounterCodingError,
    refetch: refetchEncounterCoding,
  } = useEncounterCoding(patientId as string, encounterId as string, open);
  const {
    data: encounter,
    isLoading: isEncounterLoading,
    isError: isEncounterError,
    refetch: refetchEncounter,
  } = useEncounter(encounterId, patientId);
  const encounterProgram = encounter?.program ?? null;
  const loading =
    isPatientLoading || isEncounterCodingFetching || isEncounterLoading;
  const error = isPatientError || isEncounterCodingError || isEncounterError;
  const userId = patient?.userId ? Number.parseInt(patient.userId) : 0;
  const visitCompleted =
    launch.mode === "billing" ? launch.visitCompleted : false;
  const { stepNames } = useVisitClosureSteps({
    visitDiagnosesCount: encounterCodingData?.visitDiagnoses?.length ?? 0,
    hasChildEncounters: (encounterCodingData?.childEncounters?.length ?? 0) > 0,
    encounterProgram,
    visitCompleted,
    mode: launch.mode,
  });
  const readyToRender = !loading && !error && patient && encounterCodingData;
  const showStepTitle = !loading && !error && Boolean(encounterCodingData);
  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogPortal>
        {/* HACK: this fixes an issue where a Popover inside a
        Dialog (Search) doesn't scroll.
        See https://github.com/radix-ui/primitives/issues/1159 */}
        <DialogOverlay>
          <DialogContent className="max-w-3xl">
            <DialogHeader>
              <DialogTitle>
                Visit Closure
                {showStepTitle ? ` — ${stepNames[step]}` : ""}
              </DialogTitle>
              <DialogDescription className="hidden">
                Visit closure dialog to add ICD-10 and CPT codes to the visit
              </DialogDescription>
            </DialogHeader>
            <Separator />
            {loading && (
              <>
                <Skeleton className="h-8 w-full" />
                <Skeleton className="h-8 w-full" />
                <Skeleton className="h-8 w-full" />
                <Skeleton className="h-8 w-full" />
              </>
            )}
            {!loading && error && (
              <Callout
                variant="danger"
                title="Error loading billing data."
                description="Please click here to try again."
                onClick={() => {
                  refetchPatient();
                  refetchEncounterCoding();
                  refetchEncounter();
                }}
              />
            )}
            {readyToRender && (
              <VisitClosureFlow
                patientId={patientId as string}
                userId={userId}
                encounterId={encounterId as string}
                encounterCodingData={encounterCodingData}
                encounterProgram={encounterProgram}
                setDialogOpen={handleOpenChange}
                launch={launch}
                step={step}
                setStep={setStep}
              />
            )}
          </DialogContent>
        </DialogOverlay>
      </DialogPortal>
    </Dialog>
  );
};
